import React, { useCallback, useEffect, useRef } from 'react';
import { CreateTextPlaceholderModal } from './CreateTextPlaceholderModal';
import { TextPlaceholderDropdown } from './TextPlaceholderDropdown';
import { useTextPlaceholders } from './TextPlaceholderContext';
import { useTextPlaceholderAutocomplete } from './use-text-placeholder-autocomplete';
import inline from './inline-editor.module.css';

/** Normalize line endings for scenario text stored in JSON. */
export function normalizeScenarioText(value: string): string {
  return value.replace(/\r\n/g, '\n');
}

interface InlineEditableTextProps {
  value: string;
  editMode?: boolean;
  className?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  enablePlaceholders?: boolean;
}

/**
 * Inline text field for monologue, dialogue and choice copy.
 * Uses a controlled textarea so line breaks are preserved (unlike contentEditable).
 */
export function InlineEditableText({
  value,
  editMode = true,
  className,
  onChange,
  placeholder,
  enablePlaceholders = true,
}: InlineEditableTextProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const placeholderContext = useTextPlaceholders();
  const placeholdersEnabled = enablePlaceholders && placeholderContext != null;

  const {
    state,
    createModalOpen,
    createQuery,
    setCreateModalOpen,
    updateFromTextarea,
    insertKey,
    openCreateModal,
    handleKeyDown,
    setSelectedIndex,
    close,
  } = useTextPlaceholderAutocomplete(
    textareaRef,
    value,
    placeholderContext?.entries ?? [],
    placeholdersEnabled
  );

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (editMode) adjustHeight();
  }, [value, editMode, adjustHeight]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(normalizeScenarioText(event.target.value));
      window.requestAnimationFrame(updateFromTextarea);
    },
    [onChange, updateFromTextarea]
  );

  const handleSelect = useCallback(() => {
    updateFromTextarea();
  }, [updateFromTextarea]);

  const handleKeyDownEvent = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!handleKeyDown(event)) return;

      if (event.key === 'Enter' || event.key === 'Tab') {
        if (state.selectedIndex < state.filteredEntries.length) {
          const key = state.filteredEntries[state.selectedIndex]?.key;
          if (key) onChange(insertKey(key));
        } else if (state.showCreate) {
          openCreateModal();
        }
      }
    },
    [handleKeyDown, state, onChange, insertKey, openCreateModal]
  );

  const handleCreateSubmit = useCallback(
    async (payload: { key: string; value: string; label?: string }) => {
      if (!placeholderContext) return;
      await placeholderContext.upsertPlaceholder(payload.key, payload.value, payload.label);
      onChange(insertKey(payload.key));
    },
    [placeholderContext, onChange, insertKey]
  );

  if (!editMode) {
    return <span className={className}>{value || placeholder || '…'}</span>;
  }

  return (
    <div className={inline.placeholderTextWrap}>
      <textarea
        ref={textareaRef}
        className={className}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onInput={adjustHeight}
        onKeyDown={handleKeyDownEvent}
        onClick={handleSelect}
        onKeyUp={handleSelect}
        onSelect={handleSelect}
        onBlur={() => {
          window.setTimeout(close, 150);
        }}
        rows={1}
      />
      {placeholdersEnabled ? (
        <>
          <TextPlaceholderDropdown
            state={state}
            onSelect={(key) => onChange(insertKey(key))}
            onCreate={openCreateModal}
            onHover={setSelectedIndex}
          />
          {createModalOpen ? (
            <CreateTextPlaceholderModal
              initialKey={createQuery}
              onClose={() => setCreateModalOpen(false)}
              onSubmit={handleCreateSubmit}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
